import React, { useState } from 'react'
import Layout from 'components/Layout'
import RequestProject from 'components/RequestProject'
import RequestCall from 'components/RequestCall'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import ImageGallery from 'react-image-gallery'
import 'react-image-gallery/styles/css/image-gallery.css'

import {
  Breadcrumb,
  Button,
  Segment,
  Container,
  Grid,
  Header,
  Statistic,
  Icon,
  Form,
  Dropdown,
  Embed,
  Card,
} from 'semantic-ui-react'

import items from 'data/projects.json'

const Project = ({ proj, roof }) => {
  const defRoof =
    roof === 'gable' ? 'Двускатная кровля' : roof === 'flat' ? 'Плоская кровля' : 'Нет'

  const [form, setForm] = useState({
    humidity: 'Естественная',
    dia: '180',
    roof: defRoof,
    floor: 'Нет',
  })
  const handleHum = (e, { value }) => setForm((item) => ({ ...item, humidity: value }))
  const handleDia = (e, { value }) => setForm((item) => ({ ...item, dia: value }))
  const handleRoof = (e, { value }) => setForm((item) => ({ ...item, roof: value }))
  const handleFloor = (e, { value }) => setForm((item) => ({ ...item, floor: value }))

  const sections = [
    { key: 'Главная', content: 'Главная', href: '/', as: Link },
    { key: 'Проекты', content: 'Проекты', href: '/#projects', as: Link },
    { key: proj.title, content: proj.title, active: true },
  ]

  const k1 = form.dia
  const k2 = form.humidity == 'Камерная сушка' ? 'dry' : 'wet'
  const k3 =
    form.roof == 'Плоская кровля'
      ? proj.prices.roof.flat
      : form.roof == 'Двускатная кровля'
      ? proj.prices.roof.twoside
      : 0
  const k4 = form.floor == 'Да' ? proj.prices.floor : 0

  const price_final = proj.prices[k1][k2] + k3 + k4

  const share_link = `https://aurorahomes.ru/project/${proj.id}`
  const share_title = `${proj.title} от Аврора Хоумс`

  const desc = `Сруб, диаметр бревна ${form.dia}. ${
    k2 == 'dry' ? 'Камерная сушка.' : 'Естественной влажности.'
  }\n${
    form.roof == 'Плоская кровля'
      ? 'Комплект пиломатериала для плоской кровли.'
      : form.roof == 'Двускатная кровля'
      ? 'Комплект пиломатериала для двускатной кровли.'
      : 'Без комплекта пиломатериала для кровли.'
  }\n${
    form.floor == 'Да'
      ? 'Комплект пиломатериала для перекрытий пола.'
      : 'Без комплекта пиломатериала для пола'
  }\n<b>Итого ${new Intl.NumberFormat('ru-ru').format(price_final)}руб</b>`

  const related = items
    .filter((item) => item.id !== proj.id)
    .map((item) => ({ ...item, sort: Math.abs(item.s - proj.s) }))
    .sort((a, b) => a.sort - b.sort)
    .slice(0, 3)
    .sort((a, b) => a.id - b.id)

  const preview_image = proj.images.find(
    (item) =>
      (form.roof == 'Плоская кровля' && item.type == 'plane') ||
      (form.roof == 'Двускатная кровля' && item.type == 'twoside') ||
      form.roof == 'Нет' ||
      item.type == 'any'
  ).src

  const proj_title = `${proj.title}${
    form.roof == 'Плоская кровля' ? ' с плоской крышей' : ''
  } - Аврора Хоумс`

  const proj_desc = `${proj.desc}${
    form.roof == 'Плоская кровля' ? ' с плоской кровлей' : ''
  }. Проекты домов и бань из оцилиндрованного бревна от производителя.`

  return (
    <>
      <Head>
        <title>{proj_title}</title>
        <meta property="og:title" content={proj_title} />
        <meta name="description" content={proj_desc} />
        <meta property="og:description" content={proj_desc} />
        <meta
          property="og:image"
          content={`https://aurorahomes.ru/_next/image?url=${encodeURIComponent(
            preview_image
          )}&w=640&q=75`}
        />
        <meta property="og:image:width" content="640" />
        <meta property="og:image:height" content="360" />
        <meta property="og:type" content="product" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={proj_title} />
        <meta property="twitter:description" content={proj_desc} />
        <meta
          property="twitter:image"
          content={`https://aurorahomes.ru/_next/image?url=${encodeURIComponent(
            preview_image
          )}&w=640&q=75`}
        />
      </Head>
      <Layout>
        <Segment style={{ padding: '2em 0em' }} vertical>
          <Container className={'project-module'}>
            <Breadcrumb icon="right angle" sections={sections} />
            <Header
              as="h1"
              content={proj.title}
              style={{
                fontWeight: 'bold',
              }}
            />
            <Grid stackable columns={2}>
              <Grid.Column width={9}>
                <ImageGallery
                  showBullets
                  items={proj.images
                    .filter((item) => {
                      return (
                        (form.roof == 'Плоская кровля' && item.type == 'plane') ||
                        (form.roof == 'Двускатная кровля' && item.type == 'twoside') ||
                        form.roof == 'Нет' ||
                        item.type == 'any'
                      )
                    })
                    .map((item) => ({
                      original: `/_next/image?url=${encodeURIComponent(item.src)}&w=640&q=75`,
                      thumbnail: `/_next/image?url=${encodeURIComponent(item.src)}&w=640&q=75`,
                      fullscreen: `/_next/image?url=${encodeURIComponent(item.src)}&w=1920&q=75`,
                      height: 1080,
                      width: 1920,
                    }))}
                  showPlayButton={false}
                />
              </Grid.Column>
              <Grid.Column width={7}>
                <Header as="h2" content={`${Math.ceil(price_final / 1000)} тыс.р`} />
                <Form>
                  <Form.Field>
                    <label id="diameter">Диаметр бревна</label>
                    <Dropdown
                      aria-labelledby="diameter"
                      placeholder="Диаметр бревна"
                      selection
                      defaultValue={'180'}
                      options={[
                        {
                          key: 0,
                          text: '180',
                          value: '180',
                        },
                        {
                          key: 1,
                          text: '200',
                          value: '200',
                        },
                        {
                          key: 2,
                          text: '220',
                          value: '220',
                        },
                        {
                          key: 3,
                          text: '240',
                          value: '240',
                        },
                        {
                          key: 4,
                          text: '260',
                          value: '260',
                        },
                        {
                          key: 5,
                          text: '280',
                          value: '280',
                        },
                      ]}
                      onChange={handleDia}
                      style={{ width: '250px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label id="humidity">Влажность</label>
                    <Dropdown
                      aria-labelledby="humidity"
                      placeholder="Влажность"
                      selection
                      defaultValue={'Естественная'}
                      options={[
                        {
                          key: 0,
                          text: 'Естественная',
                          value: 'Естественная',
                        },
                        {
                          key: 1,
                          text: 'Камерная сушка',
                          value: 'Камерная сушка',
                        },
                      ]}
                      onChange={handleHum}
                      style={{ width: '250px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label id="roof">Комплект пиломатериала &quot;Крыша&quot;</label>
                    <Dropdown
                      aria-labelledby="diameter"
                      placeholder="Крыша"
                      selection
                      defaultValue={defRoof}
                      options={[
                        {
                          key: 0,
                          text: 'Нет',
                          value: 'Нет',
                        },
                        {
                          key: 1,
                          text: `Плоская кровля +${Math.ceil(proj.prices.roof.flat / 1000)} тыс.р.`,
                          value: 'Плоская кровля',
                        },
                        {
                          key: 2,
                          text: `Двускатная кровля +${Math.ceil(
                            proj.prices.roof.twoside / 1000
                          )} тыс.р.`,
                          value: 'Двускатная кровля',
                        },
                      ]}
                      onChange={handleRoof}
                      style={{ width: '250px' }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label id="floor">Комплект пиломатериала &quot;Перекрытие пола&quot;</label>
                    <Dropdown
                      aria-labelledby="floor"
                      placeholder="Пол"
                      selection
                      defaultValue={'Нет'}
                      options={[
                        {
                          key: 0,
                          text: 'Нет',
                          value: 'Нет',
                        },
                        {
                          key: 1,
                          text: `Да +${Math.ceil(proj.prices.floor / 1000)} тыс.р.`,
                          value: 'Да',
                        },
                      ]}
                      onChange={handleFloor}
                      style={{ width: '250px' }}
                    />
                  </Form.Field>

                  <Statistic.Group size={'small'} widths="three" style={{ marginBottom: '0px' }}>
                    <Statistic>
                      <Statistic.Value>{proj.s}</Statistic.Value>
                      <Statistic.Label>площадь м²</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{proj.floors}</Statistic.Value>
                      <Statistic.Label>этаж{proj.floors > 1 && 'а'}</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>
                        <Icon name="child" />
                        {proj.persons}
                      </Statistic.Value>
                      <Statistic.Label>человек{proj.persons < 5 && 'а'}</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>

                  <RequestProject
                    image={proj.images[0].src}
                    trigger={
                      <Button positive size={'large'}>
                        Отправить заявку
                      </Button>
                    }
                    proj={proj}
                    desc={desc}
                  />
                  <Dropdown
                    text="Поделиться"
                    icon="send"
                    floating
                    labeled
                    button
                    className="icon large"
                    aria-label="Поделиться"
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        as={'a'}
                        icon="vk"
                        text="Вконтакте"
                        href={`https://vk.com/share.php?url=${encodeURIComponent(
                          share_link
                        )}&title=${encodeURIComponent(share_title)}&utm_source=share`}
                        target={'_blank'}
                        style={{ color: '#4c75a3' }}
                        rel="noopener"
                      />
                      {/* <Dropdown.Item
                        as={'a'}
                        icon="facebook"
                        text="Facebook"
                        href={`https://www.facebook.com/sharer.php?src=sp&u=${encodeURIComponent(
                          share_link
                        )}&title=${encodeURIComponent(share_title)}&utm_source=share`}
                        target={'_blank'}
                        style={{ color: '#3b5999' }}
                        rel="noopener"
                      /> */}
                      <Dropdown.Item
                        as={'a'}
                        icon="odnoklassniki"
                        text="Одноклассники"
                        href={`https://connect.ok.ru/offer?url=${encodeURIComponent(
                          share_link
                        )}&title=${encodeURIComponent(share_title)}&utm_source=share`}
                        target={'_blank'}
                        style={{ color: '#ed812b' }}
                        rel="noopener"
                      />
                      <Dropdown.Item
                        as={'a'}
                        icon="whatsapp"
                        text="WhatsApp"
                        href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                          share_title
                        )}%20${encodeURIComponent(share_link)}`}
                        target={'_blank'}
                        style={{ color: '#25D366' }}
                        rel="noopener"
                      />
                      <Dropdown.Item
                        as={'a'}
                        icon="telegram"
                        text="Telegram"
                        href={`https://t.me/share/url?url=${encodeURIComponent(
                          share_link
                        )}&title=${encodeURIComponent(share_title)}&utm_source=share2`}
                        target={'_blank'}
                        style={{ color: '#0088cc' }}
                        rel="noopener"
                      />
                    </Dropdown.Menu>
                  </Dropdown>
                </Form>
              </Grid.Column>
            </Grid>
          </Container>
          <Container text textAlign="center" style={{ paddingBottom: '5em', paddingTop: '5em' }}>
            <Header as="h3" style={{ fontSize: '2em' }}>
              Хотите изменить планировку?
            </Header>
            <p style={{ fontSize: '1.33em' }}>
              Нет ничего проще, наши архитекторы помогут воплотить ваши идеи в индивидуальное
              решение
            </p>
            <RequestCall
              trigger={
                <Button inverted color="green">
                  Заказать звонок
                </Button>
              }
            />
          </Container>
          <Container textAlign="center" style={{ paddingBottom: '5em' }}>
            <Header as="h3" style={{ fontSize: '2em', paddingBottom: '1rem' }}>
              Видео
            </Header>
            <Grid stackable columns={2} relaxed="very">
              <Grid.Column>
                <Embed source="youtube" id={proj.videos[0].id} placeholder={proj.videos[0].thumb} />
              </Grid.Column>
              <Grid.Column>
                <Embed source="youtube" id={proj.videos[1].id} placeholder={proj.videos[1].thumb} />
              </Grid.Column>
            </Grid>
          </Container>
          <Container textAlign="center" style={{ paddingBottom: '5em', fontSize: '1.2rem' }}>
            <Header as="h3" style={{ fontSize: '2rem', paddingBottom: '1rem' }}>
              Почему с нами надежно
            </Header>
            <Grid stackable columns={3} relaxed="very">
              <Grid.Column textAlign="left">
                <Icon name="factory" style={{ fontSize: '2rem' }} />
                <strong>Собственное производство.</strong> Покупая сруб у производителя вы получаете
                контроль качества на каждом этапе производства, а так же гарантию соответствия
                заявленным характеристикам.
              </Grid.Column>
              <Grid.Column textAlign="left">
                <Icon name="ruble" style={{ fontSize: '2rem' }} />
                <strong>Фиксированная цена.</strong> Стоимость строительства не меняется после
                заключения договора.
              </Grid.Column>
              <Grid.Column textAlign="left">
                <Icon name="handshake outline" style={{ fontSize: '2rem' }} />{' '}
                <strong>Гибкость.</strong> От бревна до деревянного дома под ключ, мы готовы
                выполнить работы любой сложности с гарантией.
              </Grid.Column>
            </Grid>
          </Container>
          <Container textAlign="center" style={{ paddingBottom: '5em' }}>
            <Header as="h3" style={{ fontSize: '2em', paddingBottom: '1rem' }}>
              Похожие проекты
            </Header>
            <Card.Group stackable itemsPerRow={3}>
              {related.map((item) => (
                <Link href={`/project/${item.id}`} key={item.id}>
                  <Card
                    href={`/project/${item.id}`}
                    image={
                      <Image
                        src={item.preview[0].src}
                        width={400}
                        height={Math.round(400 * (item.preview[0].height / item.preview[0].width))}
                        unoptimized
                      />
                    }
                    header={item.title}
                  />
                </Link>
              ))}
            </Card.Group>
          </Container>
        </Segment>
      </Layout>
    </>
  )
}

export const getStaticProps = async ({ params }) => {
  const id = params.options[0]
  const proj = items.find((item) => item.id === parseInt(Array.isArray(id) ? id[0] : id))

  if (!proj) {
    return {
      notFound: true,
    }
  }

  return {
    props: { proj, roof: params.options[1] || null },
  }
}

export async function getStaticPaths() {
  return {
    paths: [
      ...items.map((item) => {
        return { params: { options: [`${item.id}`] } }
      }),
      ...items.map((item) => {
        return { params: { options: [`${item.id}`, 'gable'] } }
      }),
      ...items.map((item) => {
        return { params: { options: [`${item.id}`, 'flat'] } }
      }),
    ],
    fallback: false,
  }
}

export default Project
